/**
 * Offline Storage — localStorage cache for bookings + settings
 * IndexedDB-style API using localStorage for simplicity and broad compatibility.
 */

const CACHE_KEYS = {
  BOOKINGS: 'superhost_cache_bookings',
  BOOKINGS_TS: 'superhost_cache_bookings_ts',
  SETTINGS: 'superhost_settings',
  OFFLINE_DRAFTS: 'superhost_offline_drafts',
};

/** Cache bookings array to localStorage */
export function cacheBookings(bookings) {
  try {
    localStorage.setItem(CACHE_KEYS.BOOKINGS, JSON.stringify(bookings));
    localStorage.setItem(CACHE_KEYS.BOOKINGS_TS, new Date().toISOString());
  } catch (e) {
    console.warn('[OfflineStorage] Failed to cache bookings:', e.message);
  }
}

/** Get cached bookings */
export function getCachedBookings() {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.BOOKINGS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Get cache timestamp */
export function getCacheTimestamp() {
  return localStorage.getItem(CACHE_KEYS.BOOKINGS_TS) || null;
}

/** Save a setting */
export function saveSetting(key, value) {
  try {
    const settings = JSON.parse(localStorage.getItem(CACHE_KEYS.SETTINGS) || '{}');
    settings[key] = value;
    localStorage.setItem(CACHE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch {
    // ignore
  }
}

/** Get a setting */
export function getSetting(key, defaultValue) {
  try {
    const settings = JSON.parse(localStorage.getItem(CACHE_KEYS.SETTINGS) || '{}');
    return settings[key] !== undefined ? settings[key] : defaultValue;
  } catch {
    return defaultValue;
  }
}

/** Save offline booking draft */
export function saveDraft(draft) {
  try {
    const drafts = JSON.parse(localStorage.getItem(CACHE_KEYS.OFFLINE_DRAFTS) || '[]');
    drafts.push({ ...draft, _offlineDraft: true, _draftCreatedAt: new Date().toISOString() });
    localStorage.setItem(CACHE_KEYS.OFFLINE_DRAFTS, JSON.stringify(drafts));
    return drafts.length;
  } catch {
    return 0;
  }
}

/** Get all offline drafts */
export function getDrafts() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEYS.OFFLINE_DRAFTS) || '[]');
  } catch {
    return [];
  }
}

/** Clear drafts (after successful sync) */
export function clearDrafts() {
  localStorage.removeItem(CACHE_KEYS.OFFLINE_DRAFTS);
}

/** Clear all cache */
export function clearCache() {
  Object.values(CACHE_KEYS).forEach(k => localStorage.removeItem(k));
}

/** iter148 — Purge the bookings + timestamp cache only. Called by AuthContext
 * when a property the user had access to is detected as deleted upstream, so
 * the Calendar / Analytics don't keep showing stale bookings for the removed
 * property. Settings and offline drafts are preserved. */
export function clearBookingsCache() {
  try {
    localStorage.removeItem(CACHE_KEYS.BOOKINGS);
    localStorage.removeItem(CACHE_KEYS.BOOKINGS_TS);
  } catch {
    // ignore
  }
}
